import { CustomerOrderProfitabilityLineKey } from "src/api/generated/erp/sales/customerOrder/profitability/model/customerOrderProfitabilityLineKey.ts";
import { CustomerOrderProfitabilityRowDto } from "src/api/generated/erp/sales/customerOrder/profitability/model/customerOrderProfitabilityRowDto.ts";
import { formatMoneyWithCurrency } from "src/utils/numberUtils.ts";

export interface ProfitabilityBarChartDatum {
	lineKey: CustomerOrderProfitabilityLineKey;
	budget: number;
	forecast: number;
	actual: number;
}

export interface ProfitabilityPieChartDatum {
	lineKey: CustomerOrderProfitabilityLineKey;
	value: number;
}

export interface ProfitabilityVarianceChartDatum {
	lineKey: CustomerOrderProfitabilityLineKey;
	actualVsBudget: number;
	actualVsForecast: number;
}

interface ChartPaletteTheme {
	palette: {
		primary: { main: string };
		blue: { main: string };
		secondary: { main: string };
		green: { main: string };
		error: { main: string };
		brown: { main: string };
		warning: { main: string };
		info: { main: string };
		grey: { 700: string; 500: string };
	};
}

type RowsByKey = Partial<Record<CustomerOrderProfitabilityLineKey, CustomerOrderProfitabilityRowDto>>;

export function buildCostBreakdownBarChartData(rowsByKey: RowsByKey): ProfitabilityBarChartDatum[] {
	return costBreakdownLineKeys.map((lineKey) => toBarChartDatum(lineKey, rowsByKey));
}

export function buildOverviewBarChartData(rowsByKey: RowsByKey): ProfitabilityBarChartDatum[] {
	return overviewLineKeys.map((lineKey) => toBarChartDatum(lineKey, rowsByKey));
}

export function buildActualCostDistributionData(rowsByKey: RowsByKey): ProfitabilityPieChartDatum[] {
	return actualCostDistributionLineKeys
		.map((lineKey) => ({
			lineKey,
			value: rowsByKey[lineKey]?.actual ?? 0,
		}))
		.filter((item) => item.value > 0);
}

export function buildVarianceBarChartData(rowsByKey: RowsByKey): ProfitabilityVarianceChartDatum[] {
	return varianceLineKeys.map((lineKey) => ({
		lineKey,
		actualVsBudget: (rowsByKey[lineKey]?.actual ?? 0) - (rowsByKey[lineKey]?.budget ?? 0),
		actualVsForecast: (rowsByKey[lineKey]?.actual ?? 0) - (rowsByKey[lineKey]?.forecast ?? 0),
	}));
}

export function getProfitabilityChartPalette(theme: ChartPaletteTheme): string[] {
	return [
		theme.palette.primary.main,
		theme.palette.secondary.main,
		theme.palette.blue.main,
		theme.palette.green.main,
		theme.palette.error.main,
		theme.palette.brown.main,
		theme.palette.warning.main,
		theme.palette.info.main,
		theme.palette.grey[700],
		theme.palette.grey[500],
	];
}

export interface ProfitabilityComparisonTooltipParam {
	axisValueLabel: string;
	marker: string;
	seriesName: string;
	value: number | string;
}

export function formatProfitabilityComparisonTooltip(
	params: ProfitabilityComparisonTooltipParam[],
	domesticCurrencyCode: string,
): string {
	const firstParam = params.at(0);
	if (firstParam == null) return "";

	const title = firstParam.axisValueLabel;
	const lines = params
		.map((param) => {
			return `<div>${param.marker}${param.seriesName}: <strong>${formatMoneyWithCurrency(Number(param.value), domesticCurrencyCode)}</strong></div>`;
		})
		.join("");

	return `<div><div style="font-weight: 600; margin-bottom: 4px;">${title}</div>${lines}</div>`;
}

export interface ProfitabilityPieTooltipParam {
	name: string;
	marker: string;
	value: number;
	percent: number;
}

export function formatProfitabilityPieTooltip(param: ProfitabilityPieTooltipParam, domesticCurrencyCode: string): string {
	return (
		`<div style="font-weight: 600; margin-bottom: 4px;">${param.name}</div>` +
		`<div>${param.marker}<strong>${formatMoneyWithCurrency(param.value, domesticCurrencyCode)}</strong> (${param.percent}%)</div>`
	);
}

const costBreakdownLineKeys = [
	"CUSTOMER_ORDER_LINE_COSTS",
	"PROJECT_MATERIAL_COSTS",
	"OTHER_COSTS",
] as const satisfies CustomerOrderProfitabilityLineKey[];

const overviewLineKeys = ["REVENUE", "TOTAL_COSTS", "MARGIN"] as const satisfies CustomerOrderProfitabilityLineKey[];

const varianceLineKeys = ["REVENUE", "TOTAL_COSTS", "MARGIN"] as const satisfies CustomerOrderProfitabilityLineKey[];

const actualCostDistributionLineKeys = [
	"CUSTOMER_ORDER_LINE_COSTS",
	"PROJECT_MATERIAL_COSTS",
	"PROJECT_COST_EVENTS",
	"CUSTOMER_ORDER_COST_EVENTS",
	"RECLAMATION_COSTS",
] as const satisfies CustomerOrderProfitabilityLineKey[];

function toBarChartDatum(lineKey: CustomerOrderProfitabilityLineKey, rowsByKey: RowsByKey): ProfitabilityBarChartDatum {
	return {
		lineKey,
		budget: rowsByKey[lineKey]?.budget ?? 0,
		forecast: rowsByKey[lineKey]?.forecast ?? 0,
		actual: rowsByKey[lineKey]?.actual ?? 0,
	};
}
